import type { JobCaptureSource } from '../api/jobWorkflow'

const DB_NAME = 'rbcfs-offline-photos'
const STORE = 'queue'
const DB_VERSION = 1

export type QueuedJobPhoto = {
  id: string
  jobId: string
  jobTaskId: string | null
  captureSource: JobCaptureSource
  fileName: string
  mimeType: string
  shouldAutoCheck: boolean
  attachmentType: 'photo' | 'document'
  createdAt: string
}

export type QueuedJobPhotoWithBlob = QueuedJobPhoto & { blob: Blob }

type StoredRow = QueuedJobPhoto & { blob: Blob }

/** In-memory fallback when IndexedDB is unavailable (tests, private mode). */
const memoryStore = new Map<string, StoredRow>()

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onerror = () => reject(req.error)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
  })
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T | null> {
  const db = await openDb()
  if (!db) return null
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const store = tx.objectStore(STORE)
    const req = fn(store)
    req.onerror = () => reject(req.error)
    req.onsuccess = () => resolve(req.result)
    tx.oncomplete = () => db.close()
    tx.onerror = () => reject(tx.error)
  })
}

export async function enqueueJobPhoto(params: {
  id: string
  jobId: string
  jobTaskId?: string | null
  file: File | Blob
  captureSource: JobCaptureSource
  shouldAutoCheck?: boolean
  attachmentType?: 'photo' | 'document'
  fileName?: string
  mimeType?: string
}): Promise<void> {
  const row: StoredRow = {
    id: params.id,
    jobId: params.jobId,
    jobTaskId: params.jobTaskId ?? null,
    captureSource: params.captureSource,
    fileName: params.fileName || (params.file instanceof File ? params.file.name : 'photo.jpg'),
    mimeType: params.mimeType || params.file.type || 'image/jpeg',
    shouldAutoCheck: !!params.shouldAutoCheck,
    attachmentType: params.attachmentType || 'photo',
    createdAt: new Date().toISOString(),
    blob: params.file,
  }

  const stored = await withStore<IDBValidKey>('readwrite', (store) => store.put(row))
  if (stored == null) {
    memoryStore.set(row.id, row)
  }
}

export async function listAllQueuedPhotos(): Promise<QueuedJobPhotoWithBlob[]> {
  const rows = await withStore<StoredRow[]>('readonly', (store) => store.getAll())
  if (rows == null) return Array.from(memoryStore.values())
  return rows
}

export async function listQueuedPhotosForJob(jobId: string): Promise<QueuedJobPhotoWithBlob[]> {
  const all = await listAllQueuedPhotos()
  return all.filter((row) => row.jobId === jobId)
}

export async function countQueuedPhotosForJob(jobId: string): Promise<number> {
  const all = await listAllQueuedPhotos()
  return all.filter((row) => row.jobId === jobId).length
}

export async function removeQueuedPhoto(id: string): Promise<void> {
  const removed = await withStore<undefined>('readwrite', (store) => store.delete(id))
  if (removed == null) {
    memoryStore.delete(id)
  }
}
